/**
 * CredenciaisPanel.jsx — Rê Leve
 * Aba "Credenciais" da Janela do Operador.
 * Gestão de integrações + uso/custo da OpenAI.
 */

import { useEffect, useState } from 'react';
import {
  getCredentials,
  setCredentialStatus,
  getOpenAIUsage,
  formatTokens,
  formatUSD,
  formatBRL,
} from '../lib/credentialsEngine';

const STATUS_LABEL = {
  pending:    { label: 'Aguardando',  color: 'baixa' },
  configured: { label: 'Configurada', color: 'média' },
  active:     { label: 'Ativa',       color: 'baixa' },
  error:      { label: 'Com erro',    color: 'alta' },
};

export default function CredenciaisPanel() {
  const creds = getCredentials();
  const [tick, setTick] = useState(0);
  const [usage, setUsage] = useState(null);
  const [openId, setOpenId] = useState('openai');

  useEffect(() => {
    let alive = true;
    getOpenAIUsage().then(u => { if (alive) setUsage(u); });
    return () => { alive = false; };
  }, [tick]);

  const refresh = () => setTick(t => t + 1);

  function toggleStatus(id, current) {
    const next = current === 'active' ? 'pending' : current === 'configured' ? 'active' : 'configured';
    setCredentialStatus(id, next);
    refresh();
  }

  return (
    <div className="rl-ops-panel">
      <div className="rl-ops-header">
        <div>
          <div className="rl-ops-eyebrow">Credenciais &amp; Integrações</div>
          <h1 className="rl-ops-title">Chaves e custos das ferramentas</h1>
          <p className="rl-ops-sub">
            Cada integração precisa de uma chave configurada no painel do Render. As chaves <strong>nunca</strong> ficam no frontend — só este painel registra o status. Custos da OpenAI são lidos do backend quando ativo.
          </p>
        </div>
        <div className="rl-ops-actions">
          <button className="rl-ops-refresh" onClick={refresh}>↻ Atualizar</button>
        </div>
      </div>

      {/* Uso OpenAI */}
      <section className="rl-card rl-card--mostard">
        <div className="rl-card-head">
          <span className="rl-leve-avatar">⚡</span>
          <div>
            <div className="rl-card-eyebrow">Uso OpenAI · {usage?.period || 'mês atual'}</div>
            <div className="rl-card-title">
              {usage?.source === 'backend' ? 'Dados em tempo real' : 'Sem dados ainda — backend indisponível'}
            </div>
          </div>
        </div>

        <div className="rl-kpi-grid">
          <div className="rl-kpi">
            <div className="rl-kpi-label">Tokens consumidos</div>
            <div className="rl-kpi-value">{usage ? formatTokens(usage.totalTokens) : '—'}</div>
            <div className="rl-kpi-sub">{usage ? formatTokens(usage.inputTokens) + ' in · ' + formatTokens(usage.outputTokens) + ' out' : ''}</div>
          </div>
          <div className="rl-kpi">
            <div className="rl-kpi-label">Requests</div>
            <div className="rl-kpi-value">{usage?.requests ?? '—'}</div>
            <div className="rl-kpi-sub">conversas com IA</div>
          </div>
          <div className="rl-kpi">
            <div className="rl-kpi-label">Custo USD</div>
            <div className="rl-kpi-value">{usage ? formatUSD(usage.estimatedCostUSD) : '—'}</div>
            <div className="rl-kpi-sub">estimativa gpt-4.1-mini</div>
          </div>
          <div className="rl-kpi">
            <div className="rl-kpi-label">Custo BRL</div>
            <div className="rl-kpi-value">{usage ? formatBRL(usage.estimatedCostBRL) : '—'}</div>
            <div className="rl-kpi-sub">câmbio aproximado</div>
          </div>
        </div>

        {usage?.source !== 'backend' && (
          <div style={{ marginTop: 14, padding: 12, background: '#fff', borderRadius: 10, fontSize: 13, color: 'var(--rl-ink-2)' }}>
            💡 {usage?.notes || 'Configure OPENAI_API_KEY no backend para ver dados reais. Estimativa atual: R$ 0,01 por conversa típica com gpt-4.1-mini.'}
          </div>
        )}
      </section>

      {/* Lista de credenciais */}
      <div className="rl-cred-list">
        {Object.entries(creds).map(([id, c]) => (
          <section key={id} className="rl-card rl-cred">
            <div className="rl-card-head">
              <div>
                <div className="rl-card-eyebrow">Variável: <code>{c.envVar}</code></div>
                <div className="rl-card-title">{c.name}</div>
              </div>
              <div className="rl-card-actions">
                <span className={'rl-alert-tag rl-alert-tag--' + STATUS_LABEL[c.status].color}>
                  {STATUS_LABEL[c.status].label}
                </span>
                <button className="rl-btn rl-btn--small" onClick={() => toggleStatus(id, c.status)}>
                  marcar como {c.status === 'active' ? 'pendente' : c.status === 'configured' ? 'ativa' : 'configurada'}
                </button>
              </div>
            </div>

            <div className="rl-cred-notes">{c.notes}</div>

            <button className="rl-link" onClick={() => setOpenId(openId === id ? null : id)}>
              {openId === id ? '▾' : '▸'} Como configurar
            </button>

            {openId === id && (
              <div className="rl-cred-setup">
                <ol className="rl-cred-steps">
                  {c.setupSteps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
                <div className="rl-cred-cost">
                  <strong>💰 Custo:</strong> {c.costInfo}
                </div>
              </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}
